import { Box, debounce } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RoundedButton } from "../../../common/components/styled/styled";
import { CustomerOrgCompany } from "../../../common/types/apiTypes";
import { customerOrgAndCompAutocomplete } from "../../customer/customerAPI";
import OrdersHeader from "../OrdersHeader";
import { OrderType } from "../orderAPI";
import {
  FormattedCustomerCompany,
  formattedCustomerCompanyDefaultValue,
  OrderTemplate,
  shortOrderDefaultFieldValues,
} from "../orderTypes";
import { OrdersFormBasicContainer } from "../styles";
import { FormValueTypes } from "../types/longOrderTypes";
import OrderFormBasic from "./OrderFormBasic";
import { NewOrderLong } from "./long/NewOrderLong";
import OrderFormShort from "./short/OrderFormShort";
import OrderShortDataProvider from "./short/OrderShortDataProvider";

export const NewOrderPage = () => {
  const { t } = useTranslation("common");

  const [title, setTitle] = useState(t("order:newOrder"));
  // Radio group value
  const [orderTypeValue, setOrderTypeValue] = useState<OrderType>(
    OrderType.Short,
  );
  // Order form value
  const [orderType, setOrderType] = useState<OrderType | null>(null);
  const [customerCompanyValue, setCustomerCompanyValue] = useState("");
  const [orderRowCount, setOrderRowCount] = useState<number>(0);
  const [customerCompanies, setCustomerCompanies] = useState<
    CustomerOrgCompany[]
  >([]);
  const [customerCompany, setCustomerCompany] =
    useState<FormattedCustomerCompany>(formattedCustomerCompanyDefaultValue);
  const [searchHistory, setSearchHistory] = useState<
    FormattedCustomerCompany[]
  >([]);

  useEffect(() => {
    if (customerCompanyValue.length >= 2) {
      customerOrgAndCompAutocomplete({ name: customerCompanyValue }).then(
        (res) => setCustomerCompanies(res),
      );
    }
  }, [customerCompanyValue]);

  useEffect(() => {
    const fromLocalStorage = localStorage.getItem("searchedCompanies");
    const items: FormattedCustomerCompany[] =
      fromLocalStorage && JSON.parse(fromLocalStorage);

    if (items) {
      return setSearchHistory(
        items.map((item, index: number) => ({ ...item, index })),
      );
    }

    localStorage.setItem("searchedCompanies", JSON.stringify([]));
  }, []);

  useEffect(() => {
    // show previously searched customers
    if (!customerCompanyValue) {
      resetCustomerCompanies();
    }
  }, [customerCompanyValue]);

  const handleContinue = () => {
    setOrderType(orderTypeValue as OrderType);
  };
  const handleOrderTitleChange = (name: string) => setTitle(name);
  const handleOrderTypeValueChange = (type: OrderType) =>
    setOrderTypeValue(type);
  const resetCustomerCompanies = () => setCustomerCompanies([]);

  const handleCustomerCompanyChange = (value: FormattedCustomerCompany) =>
    setCustomerCompany(value);

  const handleCustomerCompanyValChange = debounce(
    (value: string) => setCustomerCompanyValue(value),
    400,
  );

  const continueDisabled = !Boolean(orderTypeValue && customerCompany.orgId);

  const handleSetOrderRowCount = (count: number) => setOrderRowCount(count);

  // REVIEW - should this be moved to long order context (e.g. NewOrderLong)?
  const defaultValues: FormValueTypes = {
    edit: false,
    uid: "",
    isReadOnly: false,
    title,
    customerOrgId: customerCompany.orgId,
    type: orderType as OrderType,
    workLocation: { id: "", name: "" },
    orderTemplate: shortOrderDefaultFieldValues.orderTemplate as OrderTemplate,
    amount: 1,
    customerCostCenters: [],
    reasonForTemporaryEmployment: "",
    workStarts: null,
    workEnds: null,
    shortWorkTimeDescriptionFi: "",
    shortWorkTimeDescriptionEn: "",
    longWorkTimeDescriptionFi: "",
    longWorkTimeDescriptionEn: "",
    workDescriptionFi: "",
    workDescriptionEn: "",
    arrivalInstructionsFi: "",
    arrivalInstructionsEn: "",
    languages: [],
    certificates: [],
    skills: [],
    trainings: [],
    tesVariation: [],
    descriptionToEezyAndCustomer: "",
    flexibleStartUntil: "",
    targetedShiftTargetingRules: [],
  };

  return (
    <Box data-testid="orders-new-page-root" sx={{ maxWidth: "1700px" }}>
      <OrdersHeader
        handleOrderTitleChange={handleOrderTitleChange}
        title={title}
        customerCompany={customerCompany.company}
        orderRowCount={orderRowCount}
      />
      {!orderType && (
        <OrdersFormBasicContainer>
          <OrderFormBasic
            searchHistory={searchHistory}
            customerCompanies={customerCompanies}
            orderTypeValue={orderTypeValue}
            handleOrderTypeValueChange={handleOrderTypeValueChange}
            handleCustomerCompanyChange={handleCustomerCompanyChange}
            handleCustomerCompanyValChange={handleCustomerCompanyValChange}
            resetCustomerCompanies={resetCustomerCompanies}
          />
          <RoundedButton
            variant="contained"
            disabled={continueDisabled}
            onClick={handleContinue}
          >
            {t("continue")}
          </RoundedButton>
        </OrdersFormBasicContainer>
      )}
      {orderType === OrderType.Short && (
        <OrderShortDataProvider>
          <OrderFormShort
            title={title}
            customerCompany={customerCompany}
            handleSetOrderRowCount={handleSetOrderRowCount}
          />
        </OrderShortDataProvider>
      )}
      {(orderType === OrderType.FixedTerm ||
        orderType === OrderType.Continuous) && (
        <NewOrderLong
          title={title}
          defaultValues={defaultValues}
          updateOrderRowCount={handleSetOrderRowCount}
        />
      )}
    </Box>
  );
};
